/**
 * batch-67 portal-thema (FO-504, spec 204) - de pure thema-engine van het
 * portal-dashboard: welk feestelijk thema geldt op een gegeven moment voor een
 * medewerker, en de kop/tekst die daarbij hoort.
 *
 * Regels (spec §2.A, ADDENDA stream portal-thema):
 *   - De themadag loopt van 00:00 tot 24:00 Europe/Amsterdam, ongeacht het
 *     toestel: het UTC-instant wordt eerst naar de NL-kalenderdag vertaald
 *     (Intl, geen datum-library) en alles daarna rekent met die dag.
 *   - VERJAARDAG wint altijd van een feestdagthema; een dag heeft nooit twee
 *     thema's (`PORTAL_THEMA_PRIORITEIT`, eerste treffer wint).
 *   - 29 februari valt in een niet-schrikkeljaar op 28 februari
 *     (`berekenVerjaardagDatum`, dezelfde regel als de verjaardagkeuze).
 *     Zonder geboortedatum geen verjaardagthema.
 *   - Pasen komt uit de PublicHoliday-rijen (Eerste Paasdag, of Tweede Paasdag
 *     min een dag) met de Gauss-berekening als terugval.
 *   - Koningsdag is 27 april, of 26 april als de 27e op zondag valt.
 *
 * Puur: geen DB, geen I/O. De API levert de instellingen en de feestdag-rijen
 * aan; het beheerscherm gebruikt dezelfde functie voor "toon vandaag als ...".
 */

import {
  PORTAL_THEMA_DEFAULTS,
  PORTAL_THEMA_PRIORITEIT,
  valtInVastePeriode,
  type PortalThemaDTO,
  type PortalThemaInstellingen,
  type PortalThemaSoort,
} from '@streep/types'
import { berekenVerjaardagDatum, leeftijdOpVerjaardag } from './verjaardag-keuze'

// ── Invoer ────────────────────────────────────────────────────────────────────

export interface PortalThemaMedewerker {
  voornaam: string
  /** Employee.birthDate (UTC-middernacht van de geboortedag) of null. */
  geboortedatum: Date | null
}

/** Een PublicHoliday-rij zoals de API hem aanlevert (alleen datum + naam nodig). */
export interface PortalThemaFeestdag {
  date: Date
  name: string
}

export interface PortalThemaContext {
  /** Beheerinstellingen; ontbreken ze, dan gelden de defaults. */
  instellingen?: PortalThemaInstellingen
  /** PublicHoliday-rijen van het jaar van `datum` (voor Eerste Paasdag). */
  feestdagen?: ReadonlyArray<PortalThemaFeestdag>
  /**
   * Voorbeeldmodus (beheerknop "toon vandaag als {thema}"): forceer dit thema
   * op `datum`, in zijn hoogtepunt-variant en los van `actief`.
   */
  forceer?: PortalThemaSoort
}

// ── Kalenderdag in Europe/Amsterdam ───────────────────────────────────────────

export interface NlKalenderdag {
  jaar: number
  /** 1-12 */
  maand: number
  /** 1-31 */
  dag: number
  /** 'yyyy-mm-dd' */
  ymd: string
}

const NL_DAG_FORMAT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Amsterdam',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function ymdVan(jaar: number, maand: number, dag: number): string {
  return `${jaar}-${pad2(maand)}-${pad2(dag)}`
}

/**
 * De kalenderdag in Europe/Amsterdam van een UTC-instant. Dit is DE vertaalslag
 * die de themadag aan de NL-klok hangt: 2026-12-31T23:30:00Z is in Nederland al
 * 1 januari 2027.
 */
export function nlKalenderdag(datum: Date): NlKalenderdag {
  const parts = NL_DAG_FORMAT.formatToParts(datum)
  const lees = (type: string): number => {
    const p = parts.find((x) => x.type === type)
    if (!p) throw new Error(`Intl formatToParts mist veld "${type}"`)
    return parseInt(p.value, 10)
  }
  const jaar = lees('year')
  const maand = lees('month')
  const dag = lees('day')
  return { jaar, maand, dag, ymd: ymdVan(jaar, maand, dag) }
}

/** Kalenderdag uit een UTC-middernacht (voor berekende dagen zoals Pasen). */
function kalenderdagUitUtc(ms: number): NlKalenderdag {
  const d = new Date(ms)
  const jaar = d.getUTCFullYear()
  const maand = d.getUTCMonth() + 1
  const dag = d.getUTCDate()
  return { jaar, maand, dag, ymd: ymdVan(jaar, maand, dag) }
}

const DAG_MS = 86_400_000

function utcMs(d: Pick<NlKalenderdag, 'jaar' | 'maand' | 'dag'>): number {
  return Date.UTC(d.jaar, d.maand - 1, d.dag)
}

/** Hele dagen van `van` naar `naar` (negatief als `naar` eerder ligt). */
function dagenVerschil(
  van: Pick<NlKalenderdag, 'jaar' | 'maand' | 'dag'>,
  naar: Pick<NlKalenderdag, 'jaar' | 'maand' | 'dag'>,
): number {
  return Math.round((utcMs(naar) - utcMs(van)) / DAG_MS)
}

// ── Berekende feestdagen ──────────────────────────────────────────────────────

/**
 * Eerste Paasdag volgens de Gauss-berekening (Gregoriaanse kalender, met de
 * twee bekende uitzonderingen). Terugval wanneer de PublicHoliday-rijen van
 * het jaar geen paasdag bevatten.
 */
export function berekenPasen(jaar: number): NlKalenderdag {
  const a = jaar % 19
  const b = jaar % 4
  const c = jaar % 7
  const k = Math.floor(jaar / 100)
  const p = Math.floor((13 + 8 * k) / 25)
  const q = Math.floor(k / 4)
  const M = (15 - p + k - q) % 30
  const N = (4 + k - q) % 7
  const d = (19 * a + M) % 30
  const e = (2 * b + 4 * c + 6 * d + N) % 7

  let maand = 3
  let dag = 22 + d + e
  if (d === 29 && e === 6) {
    maand = 4
    dag = 19
  } else if (d === 28 && e === 6 && (11 * M + 11) % 30 < 19) {
    maand = 4
    dag = 18
  } else if (dag > 31) {
    maand = 4
    dag -= 31
  }
  return { jaar, maand, dag, ymd: ymdVan(jaar, maand, dag) }
}

const EERSTE_PAASDAG_NAAM = /^(1e|eerste)\s*paasdag$|^paaszondag$|^easter(\s+sunday)?$/i
const TWEEDE_PAASDAG_NAAM = /^(2e|tweede)\s*paasdag$|^paasmaandag$|^easter\s+monday$/i

/**
 * Eerste Paasdag uit de PublicHoliday-rijen van `jaar`: een rij "Eerste
 * Paasdag"/"1e Paasdag" wint; anders "2e Paasdag" (Tweede Paasdag) min een
 * dag. Rij-datums worden als Europe/Amsterdam-kalenderdag gelezen zodat zowel
 * UTC-middernacht (00:00Z) als NL-middernacht (23:00Z de dag ervoor) kloppen.
 * `null` = niet afleidbaar (dan Gauss).
 */
export function eerstePaasdagUitFeestdagen(
  jaar: number,
  feestdagen: ReadonlyArray<PortalThemaFeestdag> | undefined,
): NlKalenderdag | null {
  if (!feestdagen || feestdagen.length === 0) return null
  const inJaar = feestdagen
    .map((f) => ({ naam: f.name.trim(), dag: nlKalenderdag(f.date) }))
    .filter((f) => f.dag.jaar === jaar)
  const eerste = inJaar.find((f) => EERSTE_PAASDAG_NAAM.test(f.naam))
  if (eerste) return eerste.dag
  const tweede = inJaar.find((f) => TWEEDE_PAASDAG_NAAM.test(f.naam))
  if (tweede) return kalenderdagUitUtc(utcMs(tweede.dag) - DAG_MS)
  return null
}

/** Koningsdag: 27 april, of 26 april wanneer de 27e op zondag valt. */
export function berekenKoningsdag(jaar: number): NlKalenderdag {
  const zevenentwintig = new Date(Date.UTC(jaar, 3, 27))
  const dag = zevenentwintig.getUTCDay() === 0 ? 26 : 27
  return { jaar, maand: 4, dag, ymd: ymdVan(jaar, 4, dag) }
}

/**
 * Kan `dag` in de paasperiode vallen, ongeacht het jaar? Pasen ligt altijd
 * tussen 22 maart en 25 april; met de ingestelde dagen ervoor/erna geeft dat
 * het venster waarbinnen de API de PublicHoliday-rijen hoeft op te halen.
 * Buiten dat venster (of met Pasen uit) blijft de dashboard-fetch zonder
 * extra query.
 */
export function paasVensterMogelijk(
  dag: Pick<NlKalenderdag, 'jaar' | 'maand' | 'dag'>,
  instellingen: PortalThemaInstellingen = PORTAL_THEMA_DEFAULTS,
): boolean {
  if (!instellingen.PASEN.actief) return false
  const x = utcMs(dag)
  const start = Date.UTC(dag.jaar, 2, 22) - instellingen.PASEN.dagenVoor * DAG_MS
  const eind = Date.UTC(dag.jaar, 3, 25) + instellingen.PASEN.dagenNa * DAG_MS
  return x >= start && x <= eind
}

// ── Teksten ───────────────────────────────────────────────────────────────────

/**
 * Standaardteksten onder de kop. Bewust kort (hooguit ongeveer 45 tekens): zo
 * passen ze ook op een toestel van 320 px in twee regels (gemeten).
 */
const STANDAARD_TEKST: Record<Exclude<PortalThemaSoort, 'NIEUWJAAR'>, string> = {
  VERJAARDAG: 'Een fijne dag gewenst van het hele STREEP-team.',
  SINTERKLAAS: 'Een gezellige sinterklaastijd van STREEP.',
  KERST: 'STREEP wenst je warme en veilige feestdagen.',
  PASEN: 'STREEP wenst je een zonnig paasweekend.',
  KONINGSDAG: 'Oranje boven! Een fijne dag van STREEP.',
}

function eigenOfStandaard(eigen: string | null | undefined, standaard: string): string {
  const t = eigen?.trim()
  return t && t.length > 0 ? t : standaard
}

/**
 * Met leeftijd staat de naam voorop: op een smal toestel (320 px) wordt een
 * kop van meer dan twee regels afgekapt, en dan mag het staartje wegvallen,
 * niet de naam.
 */
function verjaardagTitel(voornaam: string, leeftijd: number | null): string {
  return leeftijd !== null
    ? `${voornaam}, gefeliciteerd met je ${leeftijd}e verjaardag!`
    : `Gefeliciteerd, ${voornaam}!`
}

// ── Per thema ─────────────────────────────────────────────────────────────────

function verjaardagThema(
  vandaag: NlKalenderdag,
  medewerker: PortalThemaMedewerker | null,
  instellingen: PortalThemaInstellingen,
  voornaam: string,
): PortalThemaDTO | null {
  const inst = instellingen.VERJAARDAG
  if (!inst.actief || !medewerker?.geboortedatum) return null
  const verjaardag = berekenVerjaardagDatum(medewerker.geboortedatum, vandaag.jaar)
  const isVandaag =
    verjaardag.getUTCMonth() + 1 === vandaag.maand && verjaardag.getUTCDate() === vandaag.dag
  if (!isVandaag) return null
  const leeftijd = inst.leeftijdTonen
    ? leeftijdOpVerjaardag(medewerker.geboortedatum, verjaardag)
    : null
  return {
    soort: 'VERJAARDAG',
    dag: vandaag.ymd,
    titel: verjaardagTitel(voornaam, leeftijd),
    tekst: eigenOfStandaard(inst.tekst, STANDAARD_TEKST.VERJAARDAG),
    leeftijd,
    hoogtepunt: true,
  }
}

function sinterklaasThema(
  vandaag: NlKalenderdag,
  instellingen: PortalThemaInstellingen,
): PortalThemaDTO | null {
  const inst = instellingen.SINTERKLAAS
  const md = vandaag.maand * 100 + vandaag.dag
  if (!inst.actief || !valtInVastePeriode(md, inst.van, inst.tot)) return null
  const pakjesavond = md === 1205
  return {
    soort: 'SINTERKLAAS',
    dag: vandaag.ymd,
    titel: pakjesavond ? 'Fijne pakjesavond' : 'Sinterklaas is in het land',
    tekst: eigenOfStandaard(inst.tekst, STANDAARD_TEKST.SINTERKLAAS),
    leeftijd: null,
    hoogtepunt: pakjesavond,
  }
}

function kerstThema(
  vandaag: NlKalenderdag,
  instellingen: PortalThemaInstellingen,
): PortalThemaDTO | null {
  const inst = instellingen.KERST
  const md = vandaag.maand * 100 + vandaag.dag
  if (!inst.actief || !valtInVastePeriode(md, inst.van, inst.tot)) return null
  const kerstdagen = md >= 1224 && md <= 1226
  return {
    soort: 'KERST',
    dag: vandaag.ymd,
    titel: kerstdagen ? 'Fijne kerstdagen' : 'Fijne feestdagen',
    tekst: eigenOfStandaard(inst.tekst, STANDAARD_TEKST.KERST),
    leeftijd: null,
    hoogtepunt: kerstdagen,
  }
}

function nieuwjaarThema(
  vandaag: NlKalenderdag,
  instellingen: PortalThemaInstellingen,
): PortalThemaDTO | null {
  const inst = instellingen.NIEUWJAAR
  const md = vandaag.maand * 100 + vandaag.dag
  if (!inst.actief || !valtInVastePeriode(md, inst.van, inst.tot)) return null
  // Vóór de jaarwisseling tellen we af; vanaf 1 januari is het nieuwe jaar er.
  const nieuwJaar = vandaag.maand === 12 ? vandaag.jaar + 1 : vandaag.jaar
  const dagenTot =
    vandaag.maand === 12 ? dagenVerschil(vandaag, { jaar: nieuwJaar, maand: 1, dag: 1 }) : 0
  let titel: string
  if (dagenTot <= 0) titel = 'Gelukkig nieuwjaar'
  else if (dagenTot === 1) titel = `Vanavond is het zover: op naar ${nieuwJaar}`
  else titel = `Nog ${dagenTot} dagen tot ${nieuwJaar}`
  return {
    soort: 'NIEUWJAAR',
    dag: vandaag.ymd,
    titel,
    tekst: eigenOfStandaard(
      inst.tekst,
      dagenTot <= 0
        ? 'STREEP wenst je een gezond en veilig jaar.'
        : 'Een goed uiteinde gewenst door STREEP.',
    ),
    leeftijd: null,
    hoogtepunt: dagenTot <= 1,
  }
}

function pasenThema(
  vandaag: NlKalenderdag,
  instellingen: PortalThemaInstellingen,
  feestdagen: ReadonlyArray<PortalThemaFeestdag> | undefined,
): PortalThemaDTO | null {
  const inst = instellingen.PASEN
  if (!inst.actief) return null
  const pasen = eerstePaasdagUitFeestdagen(vandaag.jaar, feestdagen) ?? berekenPasen(vandaag.jaar)
  const offset = dagenVerschil(pasen, vandaag)
  if (offset < -inst.dagenVoor || offset > inst.dagenNa) return null
  const paasdag = offset === 0 || offset === 1
  return {
    soort: 'PASEN',
    dag: vandaag.ymd,
    titel: paasdag ? 'Vrolijk Pasen' : 'Fijne paasdagen',
    tekst: eigenOfStandaard(inst.tekst, STANDAARD_TEKST.PASEN),
    leeftijd: null,
    hoogtepunt: paasdag,
  }
}

function koningsdagThema(
  vandaag: NlKalenderdag,
  instellingen: PortalThemaInstellingen,
): PortalThemaDTO | null {
  const inst = instellingen.KONINGSDAG
  if (!inst.actief) return null
  const koningsdag = berekenKoningsdag(vandaag.jaar)
  const offset = dagenVerschil(koningsdag, vandaag)
  if (offset < -inst.dagenVoor || offset > inst.dagenNa) return null
  return {
    soort: 'KONINGSDAG',
    dag: vandaag.ymd,
    titel: 'Fijne Koningsdag',
    tekst: eigenOfStandaard(inst.tekst, STANDAARD_TEKST.KONINGSDAG),
    leeftijd: null,
    hoogtepunt: offset === 0,
  }
}

/** Voorbeeldmodus: het thema in zijn hoogtepunt-variant, alsof het vandaag is. */
function voorbeeldThema(
  soort: PortalThemaSoort,
  vandaag: NlKalenderdag,
  medewerker: PortalThemaMedewerker | null,
  instellingen: PortalThemaInstellingen,
  voornaam: string,
): PortalThemaDTO {
  switch (soort) {
    case 'VERJAARDAG': {
      const inst = instellingen.VERJAARDAG
      const leeftijd =
        inst.leeftijdTonen && medewerker?.geboortedatum
          ? leeftijdOpVerjaardag(
              medewerker.geboortedatum,
              berekenVerjaardagDatum(medewerker.geboortedatum, vandaag.jaar),
            )
          : null
      return {
        soort,
        dag: vandaag.ymd,
        titel: verjaardagTitel(voornaam, leeftijd),
        tekst: eigenOfStandaard(inst.tekst, STANDAARD_TEKST.VERJAARDAG),
        leeftijd,
        hoogtepunt: true,
      }
    }
    case 'SINTERKLAAS':
      return {
        soort,
        dag: vandaag.ymd,
        titel: 'Fijne pakjesavond',
        tekst: eigenOfStandaard(instellingen.SINTERKLAAS.tekst, STANDAARD_TEKST.SINTERKLAAS),
        leeftijd: null,
        hoogtepunt: true,
      }
    case 'KERST':
      return {
        soort,
        dag: vandaag.ymd,
        titel: 'Fijne kerstdagen',
        tekst: eigenOfStandaard(instellingen.KERST.tekst, STANDAARD_TEKST.KERST),
        leeftijd: null,
        hoogtepunt: true,
      }
    case 'NIEUWJAAR':
      return {
        soort,
        dag: vandaag.ymd,
        titel: 'Gelukkig nieuwjaar',
        tekst: eigenOfStandaard(
          instellingen.NIEUWJAAR.tekst,
          'STREEP wenst je een gezond en veilig jaar.',
        ),
        leeftijd: null,
        hoogtepunt: true,
      }
    case 'PASEN':
      return {
        soort,
        dag: vandaag.ymd,
        titel: 'Vrolijk Pasen',
        tekst: eigenOfStandaard(instellingen.PASEN.tekst, STANDAARD_TEKST.PASEN),
        leeftijd: null,
        hoogtepunt: true,
      }
    case 'KONINGSDAG':
      return {
        soort,
        dag: vandaag.ymd,
        titel: 'Fijne Koningsdag',
        tekst: eigenOfStandaard(instellingen.KONINGSDAG.tekst, STANDAARD_TEKST.KONINGSDAG),
        leeftijd: null,
        hoogtepunt: true,
      }
  }
}

// ── Hoofdfunctie ──────────────────────────────────────────────────────────────

/**
 * Het feestelijke thema voor `datum` (een UTC-instant, meestal "nu") en deze
 * medewerker, of `null` als er vandaag niets te vieren is.
 *
 * Volgorde: `PORTAL_THEMA_PRIORITEIT` (verjaardag eerst), per thema alleen als
 * het in het beheer aan staat. De eerste treffer wint; een dag heeft dus nooit
 * twee thema's.
 */
export function bepaalPortalThema(
  datum: Date,
  medewerker: PortalThemaMedewerker | null,
  context: PortalThemaContext = {},
): PortalThemaDTO | null {
  const instellingen = context.instellingen ?? PORTAL_THEMA_DEFAULTS
  const vandaag = nlKalenderdag(datum)
  const voornaam = medewerker?.voornaam.trim() || 'collega'

  if (context.forceer) {
    return voorbeeldThema(context.forceer, vandaag, medewerker, instellingen, voornaam)
  }

  for (const soort of PORTAL_THEMA_PRIORITEIT) {
    let dto: PortalThemaDTO | null
    switch (soort) {
      case 'VERJAARDAG':
        dto = verjaardagThema(vandaag, medewerker, instellingen, voornaam)
        break
      case 'KONINGSDAG':
        dto = koningsdagThema(vandaag, instellingen)
        break
      case 'PASEN':
        dto = pasenThema(vandaag, instellingen, context.feestdagen)
        break
      case 'SINTERKLAAS':
        dto = sinterklaasThema(vandaag, instellingen)
        break
      case 'KERST':
        dto = kerstThema(vandaag, instellingen)
        break
      case 'NIEUWJAAR':
        dto = nieuwjaarThema(vandaag, instellingen)
        break
    }
    if (dto) return dto
  }
  return null
}

// ── Wegklikken (alleen in de browser, alleen voor die dag) ────────────────────

/** Minimale opslag-interface (localStorage in de browser; een Map in tests). */
export interface PortalThemaOpslag {
  getItem(sleutel: string): string | null
  setItem(sleutel: string, waarde: string): void
}

/** localStorage-sleutel; de waarde is `dag|soort`, dus één wegklik per themadag. */
export const PORTAL_THEMA_WEGKLIK_SLEUTEL = 'streep-portal-thema-weggeklikt'

export function portalThemaWegklikWaarde(thema: Pick<PortalThemaDTO, 'dag' | 'soort'>): string {
  return `${thema.dag}|${thema.soort}`
}

/**
 * Is dit thema voor deze dag weggeklikt? Alleen een exacte match op dag én
 * soort telt: de volgende themadag (of een ander thema op dezelfde dag, bv.
 * na een beheerwijziging) komt de banner gewoon terug. Een storing in de
 * opslag telt als "niet weggeklikt".
 */
export function isPortalThemaWeggeklikt(
  opslag: PortalThemaOpslag | null | undefined,
  thema: Pick<PortalThemaDTO, 'dag' | 'soort'>,
): boolean {
  if (!opslag) return false
  try {
    return opslag.getItem(PORTAL_THEMA_WEGKLIK_SLEUTEL) === portalThemaWegklikWaarde(thema)
  } catch {
    return false
  }
}

/** Onthoud het wegklikken voor deze dag (nooit op de server). */
export function markeerPortalThemaWeggeklikt(
  opslag: PortalThemaOpslag | null | undefined,
  thema: Pick<PortalThemaDTO, 'dag' | 'soort'>,
): void {
  if (!opslag) return
  try {
    opslag.setItem(PORTAL_THEMA_WEGKLIK_SLEUTEL, portalThemaWegklikWaarde(thema))
  } catch {
    // Privémodus of volle opslag: dan komt de banner bij de volgende keer terug.
  }
}

// ── Is dit thema (nog) van vandaag? ───────────────────────────────────────────

/**
 * Geldt het meegestuurde thema nog op dit moment? De portal rendert een
 * server-side bepaald thema; een PWA die de volgende dag wordt hervat, of een
 * offline uit de cache geserveerd dashboard, zou anders het thema van gisteren
 * tonen. Vergelijkt de Europe/Amsterdam-kalenderdag van `nu` met `thema.dag`.
 * Kan een thema alleen verbergen, nooit toevoegen (daar is de server voor).
 */
export function isPortalThemaVandaag(
  thema: Pick<PortalThemaDTO, 'dag'> | null | undefined,
  nu: Date = new Date(),
): boolean {
  if (!thema) return false
  return thema.dag === nlKalenderdag(nu).ymd
}
